/**
 * "Suggest cuts from who's speaking", in the multicam studio.
 *
 * PithPlay listens to each angle's own sound (only the audio is read) and
 * works out who's talking when (utils/media/speakingActivity.ts), stores it
 * on the set (so it's done once, and the transcript can name people), then
 * suggests cuts by the rules in suggestCuts.ts. The suggestions show on the
 * timeline in their own lane, apart from the real cuts, until the creator
 * applies or discards them. Applying is one request for the whole batch
 * (replacing the creator's cuts only if they choose to), and Undo takes the
 * whole batch back in one step.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import type { MulticamAngle, MulticamAnnotation } from '../../graphqlService';
import {
  CutWrite,
  MAX_CUT_BATCH,
  applyMulticamCuts,
  describeSpeakersError,
  saveSpeakingActivity,
  setMulticamSpeakerNames,
} from '../../services/speakersApi';
import { brand } from '../../theme';
import { pluralize } from '../../utils/format';
import { isAbortError } from '../../utils/media/byteSource';
import { analyseSpeaking, SpeakingAnalysis } from '../../utils/media/speakingActivity';
import { LevelsFailure, LevelsProgress, LevelsSource, readSpeakingLevels } from '../../utils/media/speakingLevels';
import {
  ACTIVITY_VERSION,
  SpeakingActivity,
  decodeRuns,
  defaultSpeakerName,
  encodeRuns,
  speakingBasis,
} from '../../utils/speakerActivity';
import type { TimelineLane } from '../Timeline/timelineTypes';
import {
  angleName,
  draftFromAnnotation,
  findAngle,
  isCut,
  normalizeAnnotations,
  sortAnnotations,
  toMutationInput,
} from './multicamStudioModel';
import { angleColor } from './multicamTheme';
import { LIVE_PLACEHOLDER_PREFIX } from './liveCutSession';
import { ApplyMode, ExistingCut, planApply, planSuggestedCuts, previewSegments } from './suggestCuts';
import type { SpeakersState } from './useSpeakingActivity';

export interface ListenResult {
  analysis: SpeakingAnalysis;
  failures: LevelsFailure[];
}

/** Reads every angle's sound and works out who's speaking (replaced in tests). */
export type Listen = (
  sources: LevelsSource[],
  options: { signal: AbortSignal; onProgress: (progress: LevelsProgress) => void }
) => Promise<ListenResult>;

const listenToAngles: Listen = async (sources, options) => {
  const levels = await readSpeakingLevels(sources, options);
  return { analysis: analyseSpeaking(levels.angles, levels.frameS), failures: levels.failures };
};

export interface SuggestCutsPanelProps {
  multicamSetId: string;
  angles: MulticamAngle[];
  masterKey: string;
  /** Length of the timeline, seconds. */
  duration: number;
  hasFootage: (angleKey: string, seconds: number) => boolean;
  annotations: MulticamAnnotation[];
  editable: boolean;
  speakers: SpeakersState;
  /** The studio's save queue: requests run in the order they were made. */
  runInOrder: <T>(work: () => Promise<T>) => Promise<T>;
  /** Applies a change to the studio's cuts and notes. */
  onAnnotationsChange: (change: (list: MulticamAnnotation[]) => MulticamAnnotation[]) => void;
  /** The preview lane for the timeline, or null to take it away. */
  onPreviewLane: (lane: TimelineLane | null) => void;
  /** A batch was applied or taken back. */
  onBatchSaved?: () => void;
  notify: (message: string, severity?: 'success' | 'error' | 'info' | 'warning') => void;
  listen?: Listen;
  /** The owner's plan doesn't include suggested cuts (they come with Pro Max). */
  upgradeRequired?: boolean;
}

type Phase = 'idle' | 'listening' | 'unsure' | 'preview' | 'applying' | 'applied' | 'undoing';

interface Batch {
  addedIds: string[];
  removed: MulticamAnnotation[];
  count: number;
}

const UNSURE_COPY: Record<string, string> = {
  'too-alike':
    'PithPlay couldn’t tell who’s speaking: the cameras’ sound is too alike. That happens when they share one mic, are all far from the people, or only one person talks. Pick the shots in Live cut instead.',
  'too-little-speech': 'PithPlay couldn’t find enough talking in this set to tell who’s speaking.',
  'one-angle': 'PithPlay needs the sound of at least two angles to tell who’s speaking.',
};

const LISTEN_FAILED = 'PithPlay couldn’t listen to this set.';

const SuggestCutsPanel: React.FC<SuggestCutsPanelProps> = ({
  multicamSetId,
  angles,
  masterKey,
  duration,
  hasFootage,
  annotations,
  editable,
  speakers,
  runInOrder,
  onAnnotationsChange,
  onPreviewLane,
  onBatchSaved,
  notify,
  listen = listenToAngles,
  upgradeRequired = false,
}) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState<LevelsProgress | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [failures, setFailures] = useState<LevelsFailure[]>([]);
  const [wideKey, setWideKey] = useState(masterKey);
  const [mode, setMode] = useState<ApplyMode>('fill');
  const [batch, setBatch] = useState<Batch | null>(null);
  const [namesOpen, setNamesOpen] = useState(false);
  const [nameDrafts, setNameDrafts] = useState<Record<string, string>>({});
  const [savingNames, setSavingNames] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // Stop listening when the studio closes.
  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    if (!angles.some((angle) => angle.angleKey === wideKey)) setWideKey(masterKey);
  }, [angles, masterKey, wideKey]);

  const { activity, setActivity, ownNames, setOwnNames } = speakers;
  const existing = useMemo<ExistingCut[]>(
    () => annotations
      .filter((annotation) => isCut(annotation) && Boolean(annotation.targetAngleKey))
      .map((annotation) => ({ id: annotation.id, time: annotation.timelineTimeMs / 1000, angleKey: annotation.targetAngleKey as string })),
    [annotations]
  );

  // The suggestions and what applying them would do, from the stored activity.
  const plan = useMemo(() => {
    if (!activity || !activity.confident) return null;
    const runs = decodeRuns(activity.runs);
    if (!runs) return null;
    // Before the players know the angles' lengths, the length that was listened to.
    const durationS = duration > 0 ? duration : activity.durationMs / 1000;
    const suggested = planSuggestedCuts({
      runs,
      frameS: activity.frameMs / 1000,
      durationS,
      angleKeys: angles.map((angle) => angle.angleKey),
      wideKey,
      masterKey,
      hasFootage,
    });
    const applying = planApply({ suggested, existing, mode: existing.length ? mode : 'replace', durationS, masterKey });
    return { ...applying, segments: previewSegments(applying, existing, durationS) };
  }, [activity, angles, duration, existing, hasFootage, masterKey, mode, wideKey]);

  const previewing = phase === 'preview' || phase === 'applying';
  const lane = useMemo<TimelineLane | null>(() => {
    if (!previewing || !plan || plan.segments.length === 0) return null;
    return {
      id: 'suggested-cuts',
      label: 'Suggested',
      icon: <AutoAwesomeOutlinedIcon />,
      kind: 'assets',
      maxRows: 1,
      editable: false,
      items: plan.segments.map((segment) => {
        const angle = findAngle(angles, segment.angleKey);
        const name = angle ? angleName(angle) : `Angle ${segment.angleKey}`;
        return {
          id: segment.id,
          start: segment.start,
          end: segment.end,
          label: name,
          color: angleColor(segment.angleKey),
          editable: false,
          ariaLabel: `Cut to ${name}`,
        };
      }),
    };
  }, [angles, plan, previewing]);
  useEffect(() => {
    onPreviewLane(lane);
  }, [lane, onPreviewLane]);
  useEffect(() => () => onPreviewLane(null), [onPreviewLane]);

  // ---- Listening ------------------------------------------------------------

  const startListening = useCallback(async () => {
    const sources: LevelsSource[] = angles
      .filter((angle) => Boolean(angle.videoUrl))
      .map((angle) => ({
        key: angle.angleKey,
        url: angle.videoUrl,
        timelineStartMs: angle.timelineStartMs,
        sourceInMs: angle.sourceInMs,
        sourceOutMs: angle.sourceOutMs,
      }));
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    setPhase('listening');
    setProblem(null);
    setFailures([]);
    setProgress({ fraction: 0, key: sources[0]?.key ?? masterKey });
    const basis = speakingBasis(angles);
    try {
      const { analysis, failures: unread } = await listen(sources, { signal: controller.signal, onProgress: setProgress });
      if (controller.signal.aborted) return;
      setFailures(unread);
      if (analysis.frames === 0 || analysis.keys.length === 0) {
        setProblem(unread.length ? `${LISTEN_FAILED} ${unread[0].reason}` : `${LISTEN_FAILED} No angle has sound PithPlay can read.`);
        setPhase('idle');
        return;
      }
      const result: SpeakingActivity = {
        version: ACTIVITY_VERSION,
        frameMs: Math.round(analysis.frameS * 1000),
        durationMs: analysis.frames * Math.round(analysis.frameS * 1000),
        angleKeys: analysis.keys,
        confident: analysis.confident,
        reason: analysis.reason,
        runs: encodeRuns(analysis.runs),
        basis,
      };
      try {
        // Stored, so it's worked out once and the transcript can name people.
        setActivity(await saveSpeakingActivity(multicamSetId, result));
      } catch (error) {
        if (error instanceof Error && (error as { code?: string }).code === 'MULTICAM_SPEAKING_STALE') {
          setProblem(describeSpeakersError(error, LISTEN_FAILED));
          setPhase('idle');
          return;
        }
        console.warn('Could not store who is speaking:', error);
        notify('Couldn’t save who’s speaking, so the transcript won’t name people yet. The suggestions still work.', 'warning');
        setActivity(result);
      }
      setPhase(result.confident ? 'preview' : 'unsure');
    } catch (error) {
      if (isAbortError(error) || controller.signal.aborted) return;
      console.error('Listening to the angles failed:', error);
      setProblem(describeSpeakersError(error, LISTEN_FAILED));
      setPhase('idle');
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, [angles, listen, masterKey, multicamSetId, notify, setActivity]);

  const suggest = () => {
    if (activity) setPhase(activity.confident ? 'preview' : 'unsure');
    else void startListening();
  };

  const cancelListening = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setPhase('idle');
    setProgress(null);
  };

  // ---- Applying and undo ----------------------------------------------------

  /** Cuts exactly as a single add sends them (the cut category and colour). */
  const writesFor = (cuts: Array<{ time: number; angleKey: string }>): CutWrite[] =>
    cuts.map((cut) => ({
      ...toMutationInput({ type: 'SWITCH', time: cut.time, endTime: null, text: '', targetAngleKey: cut.angleKey }),
      type: 'SWITCH',
      targetAngleKey: cut.angleKey,
    }));

  const apply = async () => {
    if (!plan || plan.add.length === 0) return;
    if (plan.add.length > MAX_CUT_BATCH || plan.removeIds.length > MAX_CUT_BATCH) {
      setProblem(`That’s more than ${MAX_CUT_BATCH} cuts, more than PithPlay can apply at once.`);
      return;
    }
    // A cut made in Live cut a moment ago has no id on the server yet.
    if (plan.removeIds.some((id) => id.indexOf(LIVE_PLACEHOLDER_PREFIX) === 0)) {
      setProblem('A cut you just made is still saving. Try again in a moment.');
      return;
    }
    const removing = new Set(plan.removeIds);
    const removed = annotations.filter((annotation) => removing.has(annotation.id));
    setPhase('applying');
    setProblem(null);
    try {
      const result = await runInOrder(() => applyMulticamCuts(multicamSetId, writesFor(plan.add), plan.removeIds));
      const gone = new Set(result.removedIds);
      const added = normalizeAnnotations(result.added);
      onAnnotationsChange((list) => sortAnnotations(list.filter((annotation) => !gone.has(annotation.id)).concat(added)));
      setBatch({ addedIds: added.map((annotation) => annotation.id), removed: removed.filter((annotation) => gone.has(annotation.id)), count: added.length });
      setPhase('applied');
      onBatchSaved?.();
      notify(`${pluralize(added.length, 'cut')} applied`);
    } catch (error) {
      console.error('Applying suggested cuts failed:', error);
      setProblem(describeSpeakersError(error, 'Couldn’t apply the cuts.'));
      setPhase('preview');
    }
  };

  const undo = async () => {
    if (!batch) return;
    setPhase('undoing');
    setProblem(null);
    try {
      // The creator's cuts come back as they were (reasons included), with new ids.
      const restore: CutWrite[] = batch.removed.map((annotation) => ({
        ...toMutationInput(draftFromAnnotation(annotation)),
        type: 'SWITCH',
        targetAngleKey: String(annotation.targetAngleKey),
      }));
      const result = await runInOrder(() => applyMulticamCuts(multicamSetId, restore, batch.addedIds));
      const gone = new Set(batch.addedIds);
      const back = normalizeAnnotations(result.added);
      onAnnotationsChange((list) => sortAnnotations(list.filter((annotation) => !gone.has(annotation.id)).concat(back)));
      setBatch(null);
      setPhase('idle');
      onBatchSaved?.();
      notify('Suggested cuts undone');
    } catch (error) {
      console.error('Undoing suggested cuts failed:', error);
      setProblem(describeSpeakersError(error, 'Couldn’t undo the cuts.'));
      setPhase('applied');
    }
  };

  // ---- Names ----------------------------------------------------------------

  const openNames = () => {
    setNameDrafts({ ...ownNames });
    setNamesOpen(true);
  };

  const saveNames = async () => {
    const changed = angles
      .filter((angle) => (nameDrafts[angle.angleKey] ?? '').trim() !== (ownNames[angle.angleKey] ?? '').trim())
      .map((angle) => ({ angleKey: angle.angleKey, speakerName: (nameDrafts[angle.angleKey] ?? '').trim() }));
    if (changed.length === 0) {
      setNamesOpen(false);
      return;
    }
    setSavingNames(true);
    try {
      const saved = await setMulticamSpeakerNames(multicamSetId, changed);
      const next: Record<string, string> = {};
      saved.forEach((angle) => {
        next[angle.angleKey] = angle.speakerName ?? '';
      });
      setOwnNames(next);
      setNamesOpen(false);
      notify('Names saved');
    } catch (error) {
      console.error('Saving speaker names failed:', error);
      notify(describeSpeakersError(error, 'Couldn’t save the names.'), 'error');
    } finally {
      setSavingNames(false);
    }
  };

  // ---- Render ---------------------------------------------------------------

  const busy = phase === 'listening' || phase === 'applying' || phase === 'undoing';
  const progressAngle = progress ? findAngle(angles, progress.key) : undefined;
  const count = plan?.add.length ?? 0;
  const replacing = existing.length > 0 && mode === 'replace';

  let body: React.ReactNode;
  if (phase === 'listening') {
    const percent = Math.round((progress?.fraction ?? 0) * 100);
    body = (
      <Box role="status" aria-live="polite">
        <LinearProgress variant="determinate" value={percent} aria-label="Listening progress" sx={{ mt: 1.25 }} />
        <Typography variant="body2" sx={{ mt: 1, color: 'text.primary' }}>
          Listening to {progressAngle ? angleName(progressAngle) : 'the angles'}… {percent}%
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Only the sound is read, so it takes a minute or two for an hour of video. You can keep working.
        </Typography>
        <Button size="small" color="inherit" onClick={cancelListening} sx={{ mt: 1, ml: -1 }}>
          Cancel
        </Button>
      </Box>
    );
  } else if (phase === 'unsure') {
    body = (
      <>
        <Alert severity="info" sx={{ mt: 1.25 }}>
          {UNSURE_COPY[String(activity?.reason)] ?? UNSURE_COPY['too-alike']}
          {failures.map((failure) => (
            <Box key={failure.key} component="span" sx={{ display: 'block', mt: 0.5 }}>
              {`Angle ${failure.key}: ${failure.reason}`}
            </Box>
          ))}
        </Alert>
        <Box sx={{ display: 'flex', gap: 1, mt: 1.25 }}>
          <Button size="small" variant="outlined" onClick={() => void startListening()}>
            Listen again
          </Button>
          <Button size="small" color="inherit" onClick={() => setPhase('idle')}>
            Close
          </Button>
        </Box>
      </>
    );
  } else if (previewing) {
    body = (
      <Box sx={{ mt: 1.25 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }} data-testid="suggested-cut-count">
          {count === 0 ? 'No cuts to suggest' : pluralize(count, 'suggested cut')}
          {count > 0 && replacing ? `, replacing your ${pluralize(existing.length, 'cut')}` : ''}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {count === 0
            ? (existing.length && mode === 'fill'
              ? 'Your cuts already cover the set. Choose Replace them all to start from the suggestions.'
              : 'One person does the talking here, so there’s nothing to cut to.')
            : 'They’re in the Suggested lane of the timeline, apart from your cuts. Play it through to check them.'}
        </Typography>
        {failures.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {failures.map((failure) => `Angle ${failure.key} wasn’t heard: ${failure.reason}`).join(' ')}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', mt: 1.5 }}>
          <TextField
            select
            size="small"
            label="Wide shot"
            value={wideKey}
            onChange={(event) => setWideKey(event.target.value)}
            helperText="For crosstalk, laughter and pauses"
            disabled={phase === 'applying'}
            sx={{ minWidth: 200 }}
          >
            {angles.map((angle) => (
              <MenuItem key={angle.angleKey} value={angle.angleKey}>
                {angleName(angle)}
              </MenuItem>
            ))}
          </TextField>
          {existing.length > 0 && (
            <RadioGroup
              value={mode}
              onChange={(event) => setMode(event.target.value as ApplyMode)}
              aria-label={`Your ${pluralize(existing.length, 'cut')}`}
            >
              <FormControlLabel
                value="fill"
                control={<Radio size="small" />}
                disabled={phase === 'applying'}
                label={<Typography variant="body2">Keep my cuts, fill stretches without any</Typography>}
              />
              <FormControlLabel
                value="replace"
                control={<Radio size="small" />}
                disabled={phase === 'applying'}
                label={<Typography variant="body2">Replace all my cuts</Typography>}
              />
            </RadioGroup>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
          <Button
            variant="contained"
            onClick={() => void apply()}
            disabled={count === 0 || phase === 'applying'}
            startIcon={phase === 'applying' ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {phase === 'applying' ? 'Applying…' : `Apply ${pluralize(count, 'cut')}`}
          </Button>
          <Button color="inherit" onClick={() => setPhase('idle')} disabled={phase === 'applying'}>
            Discard
          </Button>
          <Button size="small" color="inherit" onClick={() => void startListening()} disabled={phase === 'applying'} sx={{ ml: 'auto' }}>
            Listen again
          </Button>
        </Box>
      </Box>
    );
  } else if (phase === 'applied' || phase === 'undoing') {
    body = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1.25 }}>
        <Typography variant="body2" sx={{ color: 'text.primary', mr: 'auto' }}>
          {batch ? `Applied ${pluralize(batch.count, 'cut')}.` : 'Applied.'} Undo takes them all back.
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => void undo()}
          disabled={phase === 'undoing' || !batch}
          startIcon={phase === 'undoing' ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {phase === 'undoing' ? 'Undoing…' : 'Undo'}
        </Button>
        <Button size="small" color="inherit" onClick={() => setPhase('idle')} disabled={phase === 'undoing'}>
          Done
        </Button>
      </Box>
    );
  } else if (upgradeRequired) {
    body = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 0.5 }} data-testid="suggest-cuts-upgrade">
        <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 260px', minWidth: 0 }}>
          Suggested cuts come with Pro Max: PithPlay listens to each camera’s own sound, works out who’s talking, and
          suggests a cut to them.
        </Typography>
        <Button variant="outlined" href="/billing" startIcon={<AutoAwesomeOutlinedIcon />}>
          Upgrade
        </Button>
      </Box>
    );
  } else {
    body = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 0.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 260px', minWidth: 0 }}>
          PithPlay listens to each camera’s own sound, works out who’s talking, and suggests a cut to them. Nothing changes
          until you apply it.
        </Typography>
        <Button variant="outlined" onClick={suggest} disabled={!editable || busy} startIcon={<AutoAwesomeOutlinedIcon />}>
          Suggest cuts
        </Button>
      </Box>
    );
  }

  return (
    <Box
      data-testid="suggest-cuts-panel"
      sx={{ border: `1px solid ${brand.border}`, borderRadius: 2, bgcolor: 'background.paper', px: 2, py: 1.5 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" component="h2" sx={{ fontWeight: 600, color: 'text.primary', mr: 'auto' }}>
          Suggest cuts from who’s speaking
        </Typography>
        {!namesOpen && (
          <Button size="small" color="inherit" startIcon={<BadgeOutlinedIcon />} onClick={openNames} disabled={!editable || busy}>
            Names
          </Button>
        )}
      </Box>
      {problem && (
        <Alert severity="error" sx={{ mt: 1.25 }} onClose={() => setProblem(null)}>
          {problem}
        </Alert>
      )}
      {body}
      <Collapse in={namesOpen} unmountOnExit>
        <Box component="form" sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${brand.border}` }} onSubmit={(event) => { event.preventDefault(); void saveNames(); }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Who’s on each camera
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
            The transcript names who says each line once PithPlay knows who’s speaking. Leave a name empty to use the angle’s label.
          </Typography>
          <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(180px, 1fr))' } }}>
            {angles.map((angle) => (
              <TextField
                key={angle.angleKey}
                size="small"
                label={angleName(angle)}
                value={nameDrafts[angle.angleKey] ?? ''}
                placeholder={defaultSpeakerName(angle)}
                onChange={(event) => setNameDrafts((previous) => ({ ...previous, [angle.angleKey]: event.target.value }))}
                inputProps={{ maxLength: 60 }}
                InputLabelProps={{ shrink: true }}
              />
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: 1.25 }}>
            <Button size="small" variant="contained" type="submit" disabled={savingNames}>
              {savingNames ? 'Saving…' : 'Save names'}
            </Button>
            <Button size="small" color="inherit" onClick={() => setNamesOpen(false)} disabled={savingNames}>
              Cancel
            </Button>
          </Box>
          {!activity && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Suggest cuts works out who’s speaking.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default SuggestCutsPanel;
